import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import { Email, ArrowBack, LockReset, CheckCircleOutline } from '@mui/icons-material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { showToast } from '../../store/uiSlice';
import axiosClient from '../../api/axiosClient';
import Logo from '../../assets/header-logo.svg';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(''); // For dev only
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axiosClient.post('/auth/forgot-password', { email });
      const data = response.data;
      setResetToken(data.resetToken || '');
      setSubmitted(true);
      dispatch(showToast({ message: 'Reset link generated successfully!', type: 'success' }));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background:
            'radial-gradient(circle at 20% 50%, rgba(97, 97, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0, 115, 234, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container
        maxWidth="sm"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, position: 'relative', zIndex: 1 }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'white',
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Top Gradient Bar */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
              py: 5,
              px: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'white',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <LockReset sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Forgot Password?
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, textAlign: 'center' }}>
              Enter your Gmail address and we'll generate a reset token for you.
            </Typography>
          </Box>

          {/* Form Body */}
          <Box sx={{ p: 4 }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <img src={Logo} alt="Agile Edge" style={{ height: 36 }} />
            </Box>

            <Collapse in={!submitted}>
              <Box component="form" onSubmit={handleSubmit}>
                <Collapse in={!!error}>
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Collapse>

                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your Gmail address"
                  required
                  autoComplete="email"
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    mb: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #357ABD 0%, #4AA3D5 100%)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                </Button>
              </Box>
            </Collapse>

            {/* Success State */}
            <Collapse in={submitted}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleOutline sx={{ fontSize: 56, color: '#4caf50', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Reset Token Generated!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Use the token below to reset your password.
                </Typography>

                {resetToken && (
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2, textAlign: 'left', wordBreak: 'break-all' }}>
                    <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                      Your Reset Token:
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {resetToken}
                    </Typography>
                  </Alert>
                )}

                <Button
                  component={Link}
                  to={`/reset-password${resetToken ? `?token=${resetToken}` : ''}`}
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.5,
                    mb: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #357ABD 0%, #4AA3D5 100%)',
                    },
                  }}
                >
                  Reset My Password →
                </Button>
              </Box>
            </Collapse>

            {/* Back to Login */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                component={Link}
                to="/"
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
